import { ChangeEvent, useEffect, useState, FocusEvent, Dispatch, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IKnmIndex } from 'APIPackage/KnmModule/interfaces/IKnmIndex';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmListFindKnm, knmListSetFindData } from 'KnmListPackage/MainModule/actions/knmListActions';
import { getKnmListFindData } from 'KnmListPackage/MainModule/selectors/knmListSelectors';

import { FieldBlock } from '../../../../UIPackage/FormModule/components/FieldBlock';

const ERP_ID_MIN_LENGTH = 12;

interface IKnmAutocompleteOption {
  label: string;
  value: string;
  knm: IKnmIndex;
}

const generateKnmInfoText = (knm: IKnmIndex) =>
  `${knm.erpId ?? ''} ${knm.knmType ?? ''} ${knm.organizationName ?? ''} ${knm.inn ? `(ИНН: ${knm.inn}` : ''}${
    knm.ogrn ? ` ОГРН: ${knm.ogrn}` : ''
  }) ${knm.controllingOrganization ?? ''}${knm.prosecutorOrganization ?? ''} ${formatDateLocale({
    date: knm.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knm.stopDate ? `${formatDateLocale({ date: knm.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'}`;

interface IErknmLinksProps {
  setLinkedPm: (selectedKnm: IKnmIndex | undefined) => void;
}

interface IErknmLinksStoreProps {
  knms: IKnmIndex[];
}

const ErknmLinksWithoutStore = ({ knms, setLinkedPm }: IErknmLinksProps & IErknmLinksStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [knmsAutocompleteOptions, setKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[] | undefined>();
  const [erpIdInputValue, setErpIdInputValue] = useState('');
  const [isErpIdSelected, setIsErpIdSelected] = useState(false);
  const [isFounded, setIsFounded] = useState(false);

  function findKnms(paramErpId?: string) {
    if (paramErpId && paramErpId.length >= ERP_ID_MIN_LENGTH) {
      setIsFounded(true);
      dispatch(
        knmListFindKnm(
          { erpId: paramErpId, isKnmLinkQuery: true, ignoreProsecutorOffice: true, ignoreKno: true },
          2000,
        ),
      );
    }
  }

  const debouncedGetKnms = debounce(findKnms, 3000);

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const valueEdited = value?.trim();

      setErpIdInputValue(valueEdited);
      if (!valueEdited) {
        setIsErpIdSelected(false);
      }
      debouncedGetKnms(valueEdited);
      if (!valueEdited || valueEdited.length <= ERP_ID_MIN_LENGTH) {
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetKnms],
  );

  const handleAutoCompleteSelect = useCallback(
    (value: string) => {
      return () => {
        const knmAutocompleteOption = knmsAutocompleteOptions?.find(
          lastKnmAutocompleteOption => lastKnmAutocompleteOption.value === value,
        );
        const knm = knmAutocompleteOption?.knm;

        if (knm) {
          setLinkedPm(knm);
        }

        setErpIdInputValue(value);
        debouncedGetKnms('');
        setKnmsAutocompleteOptions(undefined);
        setIsErpIdSelected(true);
      };
    },
    [debouncedGetKnms],
  );

  useEffect(() => {
    isFounded &&
      setKnmsAutocompleteOptions(
        knms.map(knm => {
          const knmText = `${generateKnmInfoText(knm)}`;

          return {
            value: knmText,
            label: knmText,
            knm,
          };
        }),
      );
  }, [knms, isFounded]);

  useUnmount(() => {
    dispatch(knmListSetFindData([]));
  });

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedGetKnms('');
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetKnms],
  );

  const handleTextInputClear = useCallback(() => {
    setErpIdInputValue('');
    setIsErpIdSelected(false);
    setLinkedPm(undefined);
  }, []);

  return (
    <FieldBlock>
      <AutoComplete
        type="area"
        options={knmsAutocompleteOptions}
        noFoundMessage="КНМ, соответствующие указанному учетному номеру, не найдены"
        handleAutoCompleteSelect={handleAutoCompleteSelect}
      >
        <TextInput
          id="linkErpId"
          name="linkErpId"
          placeholder="Введите номер паспорта, выберите из выпадающего списка"
          clearable={isErpIdSelected}
          value={erpIdInputValue}
          onBlur={handleTextInputBlur}
          onChange={handleTextInputChange}
          onClear={handleTextInputClear}
        />
      </AutoComplete>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmLinksStoreProps => ({
  knms: getKnmListFindData(state),
});

export const PmLink = connect(mapStateToProps)(ErknmLinksWithoutStore);
